import React from 'react';
import styled from 'styled-components';
import Layout from 'layouts/master-layout';
import { GetStaticPaths, GetStaticProps } from 'next';
import HeadlineBanner from 'components/headline-banner';
import { getFrontpage, getLearningPathsAndModules } from 'contentful/contentful-api';
import { frontpageIds, products } from 'utils/constants';
import ProductCard from 'components/product-card';
import { mqFrom } from 'styles/styles';
import Button from 'components/button';
import { useRouter } from 'next/router';
import i18n, { SettingsType } from 'utils/i18n';

type BrowseAllPageProps = {
  frontpage: Frontpage;
  product?: LightProduct; // eslint-disable-line

  items: (LearningPath | Module)[];
};

export const BrowseAllPage: React.FC<BrowseAllPageProps> = ({ frontpage, items, product }) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;

  if (!product) return null;

  return (
    <Layout breadcrumb={[]} title={frontpage.meta.title} description={frontpage.meta.description}>
      <HeadlineBanner wide title={frontpage.title} subtitle={frontpage.description} />
      <StyledBrowseAll>
        <div className="outer-padding modules-list">
          <ul className="tags">
            <li>
              <Button size="small" mode={'secondary'} linkProps={{ href: `/browse` }}>
                {dictionary.browseAll}
              </Button>
            </li>

            {products.map((item) => {
              return (
                <li key={item.tag}>
                  <Button
                    size="small"
                    mode={item.tag === product.tag ? 'primary' : 'secondary'}
                    linkProps={{ href: `/browse/${item.slug}` }}
                  >
                    {item.title}
                  </Button>
                </li>
              );
            })}
          </ul>

          <div className="product-grid">
            {items
              .filter((item) => item.meta?.id)
              .map((item) => {
                return (
                  <ProductCard
                    key={item.meta.id}
                    item={item}
                    bookmarkData={
                      item.meta.type === 'Module' ? { moduleId: item.meta.id } : { learningPathId: item.meta.id }
                    }
                  ></ProductCard>
                );
              })}
          </div>
        </div>
      </StyledBrowseAll>
    </Layout>
  );
};

const StyledBrowseAll = styled.section`
  margin-bottom: 3rem;

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin: 1rem 0;
  }

  .product-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;

    ${mqFrom.mobile} {
      grid-template-columns: repeat(2, 1fr);
    }
    ${mqFrom.tablet} {
      grid-template-columns: repeat(3, 1fr);
    }
    /* ${mqFrom.desktop} {
      grid-template-columns: repeat(4, 1fr);
    } */
  }
`;

export const getStaticProps: GetStaticProps<BrowseAllPageProps, { product: string }> = async ({ locale, params }) => {
  const frontpage = await getFrontpage({ locale: locale as AcceptedLocales, frontpageId: frontpageIds.browse });
  const prod = products.find((p) => p.slug === params?.product);

  const items = await getLearningPathsAndModules({
    locale: locale as AcceptedLocales,
  });

  console.log('browse-product-test',  {
    frontpage,
    product: prod,
    items: items.filter((item) => item.meta.tags.some((tag) => tag.name === prod?.tag)),
  })

  return {
    props: {
      frontpage,
      product: prod,
      items: items.filter((item) => item.meta.tags.some((tag) => tag.name === prod?.tag)),
    },
    revalidate: 1,
  };
};

type Params = { product: string };

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  return {
    paths: products.map((p) => {
      return { params: { product: p.slug } };
    }),
    fallback: 'blocking',
  };
};

export default BrowseAllPage;
